import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translate, localizeRoleName, localizeRoleDesc, localizeWinner, SUPPORTED_LANGUAGES } from './translations';

const LanguageContext = createContext({
  lang: 'ru',
  setLang: () => {},
  t: (key) => key,
  tRole: (name) => name,
  tRoleDesc: (name, fb) => fb,
  tWinner: (team) => team,
});

const STORAGE_KEY = 'vangard.lang';

function detectInitialLanguage() {
  if (typeof window === 'undefined') return 'en';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED_LANGUAGES.some((l) => l.code === stored)) return stored;
  const browser = (navigator.language || 'en').toLowerCase();
  if (browser.startsWith('uk')) return 'uk';
  if (browser.startsWith('ru')) return 'ru';
  return 'en';
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState('en');

  useEffect(() => {
    setLangState(detectInitialLanguage());
  }, []);

  const setLang = useCallback((code) => {
    setLangState(code);
    if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, code);
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback((key, params) => translate(key, lang, params), [lang]);
  const tRole = useCallback((name) => localizeRoleName(name, lang), [lang]);
  const tRoleDesc = useCallback((name, fb) => localizeRoleDesc(name, lang, fb), [lang]);
  const tWinner = useCallback((team) => localizeWinner(team, lang), [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, tRole, tRoleDesc, tWinner }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  return useContext(LanguageContext);
}

export { SUPPORTED_LANGUAGES };
